import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/salesRequestMutations"

import { DeleteDraftQuoteRequestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteDraftQuoteRequestReturn {
  deleteDraftQuoteRequest: (quoteGroupId: number) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteDraftQuoteRequest = (): UseDeleteDraftQuoteRequestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(DeleteDraftQuoteRequestDocument)

  const deleteDraftQuoteRequest = useCallback(
    async (quoteGroupId: number): Promise<boolean> => {
      const { data, error } = await executeMutation({ quoteGroupId })
      const output = data?.salesPipeline.deleteDraftQuoteRequest

      if (error || output?.__typename === "DeleteDraftQuoteRequestFailure") {
        const message = output?.__typename === "DeleteDraftQuoteRequestFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to discard the draft request. Please try again.",
        })
        return false
      }

      // Background cleanup — no success toast, the user didn't ask for this.
      return true
    },
    [executeMutation, notify]
  )

  return { deleteDraftQuoteRequest, isDeleting: fetching }
}
